/**
 * Copyright 2025, IntraLinks, Inc. All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  IlCard,
  IlIconInitializer,
  IlButton,
  IlStates,
  IlLoading
} from 'il-framework-component';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../../app/store';
import Wrapper from '../../../../app/shared/components/Wrapper/Wrapper';
import { getConfig } from '../../../../app/shared/utils/config.util';
import { requireUserId } from '../../../Profile/profileSlice';
import {
  elevateUserSession, EnrichmentStatus, ENRICHMENT_OUTCOME_KEY,
  resetEnrichmentState, setEnrichmentError, setEnrichmentReturnUrl, setEnrichmentStatus
} from '../../setupSecuritySlice';
import { DEFAULT_POPUP_CLOSE_TIME, I18nNamespaces } from '../../../../app/shared/utils/constants.util';
import { closeNow, getFlowSnapshot } from '../../../../app/shared/utils/popup.util';
import Repos from '../../../../infrastructure/repositories/Repos';
import styles from './LoaEnrichmentResult.scss';

const LoaEnrichmentResult: React.FC = () => {
  const { t, ready: i18nReady } = useTranslation(I18nNamespaces.LOA_ENRICHMENT);
  const dispatch = useDispatch();
  // Prevents the sessionStorage restore logic from running more than once per mount
  const hasRestoredRef = useRef(false);
  // Guards against a race condition: when the countdown/close resets status to Idle,
  // React re-renders and the restore effect would fire again reading an already-cleared
  // sessionStorage, causing a spurious redirect to loginUrl instead of returnUrl.
  const isNavigatingRef = useRef(false);

  const status = useSelector((s: RootState) => s.setupSecurity.enrichmentStatus);
  const errorKey = useSelector((s: RootState) => s.setupSecurity.enrichmentError);
  const loaProof = useSelector((s: RootState) => s.setupSecurity.enrichmentLoaProof);
  const targetLevel = useSelector((s: RootState) => s.setupSecurity.enrichmentTargetLevel);
  const returnUrl = useSelector((s: RootState) => s.setupSecurity.enrichmentReturnUrl);

  const { mode, applicationId } = getFlowSnapshot();
  const isPopupMode = mode === 'popup';
  const [secondsLeft, setSecondsLeft] = useState(DEFAULT_POPUP_CLOSE_TIME);

  const isPending = !i18nReady || status === EnrichmentStatus.Idle || status === EnrichmentStatus.Loading;
  const isDone = status === EnrichmentStatus.Success || status === EnrichmentStatus.Error;
  const isSuccess = status === EnrichmentStatus.Success;

  const { iamHost } = getConfig();
  const loginUrl = applicationId
    ? `${iamHost}/idp/login/?applicationid=${applicationId}`
    : `${iamHost}/idp/login`;

  const logoutAndRedirect = () => {
    Repos.instance.logoutRepository.logout().finally(() => {
      window.location.replace(loginUrl);
    });
  };

  // Restore enrichment outcome from sessionStorage on page reload
  useEffect(() => {
    if (isNavigatingRef.current) return;
    if (hasRestoredRef.current) return;
    if (status !== EnrichmentStatus.Idle) return;
    hasRestoredRef.current = true;

    const stored = sessionStorage.getItem(ENRICHMENT_OUTCOME_KEY);
    if (!stored) {
      // No persisted outcome — flow is unrecoverable, logout and redirect to login
      logoutAndRedirect();
      return;
    }

    // Restore the outcome and let the countdown run
    try {
      const outcome = JSON.parse(stored);
      if (outcome.returnUrl) {
        dispatch(setEnrichmentReturnUrl(outcome.returnUrl));
      }
      if (outcome.status === 'success') {
        dispatch(setEnrichmentStatus(EnrichmentStatus.Success));
      } else {
        dispatch(setEnrichmentStatus(EnrichmentStatus.Error));
        if (outcome.errorKey) {
          dispatch(setEnrichmentError(outcome.errorKey));
        }
      }
    } catch {
      logoutAndRedirect();
    }
  }, [status, dispatch, isPopupMode, loginUrl]);

  useEffect(() => {
    const runElevation = async () => {
      if (status !== EnrichmentStatus.Loading) return;
      if (!loaProof || !targetLevel) return;

      const userId = await dispatch(requireUserId() as any);
      await dispatch(elevateUserSession({ loaProof }, targetLevel, userId as string));
    };

    runElevation();
  }, [status, loaProof, targetLevel, dispatch]);

  useEffect(() => {
    if (!isDone) return;

    if (secondsLeft <= 0) {
      isNavigatingRef.current = true;
      dispatch(resetEnrichmentState());
      sessionStorage.removeItem(ENRICHMENT_OUTCOME_KEY);

      if (isPopupMode) {
        closeNow();
        return;
      }

      if (isSuccess && returnUrl) {
        window.location.replace(returnUrl);
      } else {
        logoutAndRedirect();
      }
      return;
    }

    const id = window.setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    // eslint-disable-next-line consistent-return
    return () => window.clearTimeout(id);
  }, [isDone, secondsLeft, dispatch, isPopupMode, isSuccess, returnUrl, applicationId]);

  const title = isSuccess
    ? t('loaEnrichment.sessionResult.enrichmentSuccessTitle')
    : t('loaEnrichment.sessionResult.enrichmentFailTitle');

  const description = isSuccess
    ? t('loaEnrichment.sessionResult.enrichmentSuccessDescription')
    : t(errorKey || 'loaEnrichment.sessionResult.enrichmentFailDescription');

  const illustration = isSuccess ? 'contract' : 'error';

  const handleCloseClick = () => {
    isNavigatingRef.current = true;
    dispatch(resetEnrichmentState());
    sessionStorage.removeItem(ENRICHMENT_OUTCOME_KEY);

    if (isPopupMode) {
      closeNow();
      return;
    }

    if (returnUrl) {
      window.location.replace(returnUrl);
    } else {
      logoutAndRedirect();
    }
  };

  const getCountdownText = () => {
    if (secondsLeft > 0) {
      return mode === 'redirect'
        ? t('loaEnrichment.sessionResult.autoRedirectIn', { seconds: secondsLeft })
        : t('loaEnrichment.sessionResult.autoCloseIn', { seconds: secondsLeft });
    }
    return mode === 'redirect'
      ? t('loaEnrichment.sessionResult.autoRedirectHint')
      : t('loaEnrichment.sessionResult.autoCloseHint');
  };

  return (
    <>
      <IlIconInitializer />
      <Wrapper>
        <IlCard className={styles.cardForm}>
          {isPending ? (
            <IlLoading withOverlay />
          ) : (
            <>
              <div className={styles.stateSectionContainer}>
                <IlStates
                  illustration={illustration}
                  title={title}
                  description={description}
                />
              </div>

              <div className={styles.closeBtnContainer}>
                <p className={styles.helpText}>
                  {getCountdownText()}
                </p>
                {isSuccess && (
                  <IlButton kind='primary' onClick={handleCloseClick}>
                    {mode === 'redirect'
                      ? t('loaEnrichment.sessionResult.returnNowBtn')
                      : t('loaEnrichment.sessionResult.closeNowBtn')}
                  </IlButton>
                )}
              </div>
            </>
          )}
        </IlCard>
      </Wrapper>
    </>
  );
};

export default LoaEnrichmentResult;
